import { randomId } from "../../helpers/random";
import { findPlaceholderPosition, insertUploadPlaceholder, removeUploadPlaceholder, } from "./plugins/filestack-upload.plugin";
import { filestackPickerPluginKey } from "./plugins/filestack-picker.plugin";
/**
 * Start the upload of an attachment, by opening the filestack picker.
 * Once uploaded and picker is closed, replaces the selection with the attached file.
 */
export function uploadAttachment(view, mode) {
    const { from, to } = view.state.selection;
    view.dispatch(view.state.tr
        .setMeta(filestackPickerPluginKey, { mode })
        .deleteRange(from, to));
}
/**
 * Uploads the given files to filestack, adding them as nodes to the editor.
 */
export function uploadFiles(files, pos, view, filestackService) {
    for (let i = 0; i < files.length; i++) {
        uploadFile(files.item(i), pos, view, filestackService);
    }
}
export function uploadFile(file, pos, view, filestackService) {
    if (!filestackService.validateUploadingFile(file)) {
        return;
    }
    const id = randomId();
    view.dispatch(insertUploadPlaceholder(view.state.tr, id, pos, file));
    filestackService
        .uploadFile(file)
        .then((uploadedFile) => {
        handleUploadedFile(view, id, uploadedFile);
    })
        .catch(() => {
        handleUploadError(view, id);
    });
}
function handleUploadedFile(view, id, file) {
    const pos = findPlaceholderPosition(view.state, id);
    if (!pos) {
        // If placeholder was deleted, don't create node
        return;
    }
    view.dispatch(removeUploadPlaceholder(view.state.tr, id).insert(pos, createNodeFromUploadedFile(view, file)));
}
function handleUploadError(view, id) {
    const pos = findPlaceholderPosition(view.state, id);
    if (!pos) {
        return;
    }
    view.dispatch(removeUploadPlaceholder(view.state.tr, id));
}
export function createNodeFromUploadedFile(view, file, mode = "auto") {
    if (mode === "attachment") {
        return createAttachmentNode(view, file);
    }
    if (mode === "image" || isImageFile(file)) {
        return createImageNode(view, file);
    }
    if (mode === "video" || isVideoFile(file)) {
        return createVideoNode(view, file);
    }
    return createAttachmentNode(view, file);
}
function createAttachmentNode(view, file) {
    const schema = view.state.schema;
    return schema.nodes.attachment.create({ file });
}
function createImageNode(view, file) {
    const schema = view.state.schema;
    return schema.nodes.image.create({ file });
}
function createVideoNode(view, file) {
    const schema = view.state.schema;
    return schema.nodes.video.create({
        file,
    });
}
/**
 * Process a drop event and uploads any dropped files.
 * Returns `true` if any file was uploaded.
 */
export function handleDragDropEvent(event, view, filestackService) {
    var _a;
    if (!view.editable) {
        return false;
    }
    const files = event.dataTransfer.files;
    if (files.length) {
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY });
        uploadFiles(files, (_a = pos === null || pos === void 0 ? void 0 : pos.pos) !== null && _a !== void 0 ? _a : view.state.selection.from, view, filestackService);
        event.preventDefault();
        event.stopPropagation();
        return true;
    }
    return false;
}
/**
 * Process a paste event and uploads any pasted files.
 * Returns `true` if any file was uploaded.
 */
export function handlePasteEvent(event, view, filestackService) {
    var _a, _b;
    if ((_b = (_a = event.clipboardData) === null || _a === void 0 ? void 0 : _a.types) === null || _b === void 0 ? void 0 : _b.includes("text/rtf")) {
        // do not convert pasted rtf to image (pasted text from OneDrive or similar RTF editors)
        return false;
    }
    const files = event.clipboardData.files;
    if (files.length) {
        uploadFiles(files, view.state.selection.from, view, filestackService);
        event.preventDefault();
        event.stopPropagation();
        return true;
    }
    return false;
}
export function isImageFile(file) {
    var _a, _b;
    return (_b = (_a = file.metadata) === null || _a === void 0 ? void 0 : _a.mimetype) === null || _b === void 0 ? void 0 : _b.startsWith("image/");
}
export function isVideoFile(file) {
    var _a, _b;
    return (_b = (_a = file.metadata) === null || _a === void 0 ? void 0 : _a.mimetype) === null || _b === void 0 ? void 0 : _b.startsWith("video/");
}
